import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import { Modal, Table, VideoCameraIcon } from "@dealer-center/core-components";
import type {
  ColorTableProps,
  ImageRowWithType
} from "@dealer-center/inventory-pages-gvm-interface";
import { useState } from "react";
import { useIsGvmRwAccessUser } from "../util/gvmUserUtil";
import { getProvider, isIoF, isVideo } from "./imageHelper";

export const ColorTable = ({
  colors,
  isLoading,
  shotTypeLookup
}: ColorTableProps) => {
  const [showModal, setShowModal] = useState(false);
  const [url, setUrl] = useState("");
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const [selectedRows, setSelectedRows] = useState<ImageRowWithType[]>([]);

  const onOpenModal = (url: string) => {
    setUrl(url);
    setShowModal(true);
  };

  const columns: TypeSafeTableColumns<ImageRowWithType> = [
    {
      key: "color",
      dataIndex: "hex",
      title: "Color",
      align: "left",
      render: (value, record) => (
        <div
          style={{
            width: "100%",
            minHeight: "100px",
            backgroundColor: `#${record.hex}`,
            display: "block"
          }}
          title={`#${record.hex}`}
        />
      )
    },
    {
      key: "name",
      dataIndex: "name",
      title: "Name",
      align: "left"
    },
    {
      key: "hex",
      dataIndex: "hex",
      title: "Hex",
      align: "left",
      render: (value, record) =>
        record.type === "color" ? "#" + record.hex : ""
    },
    {
      key: "code",
      dataIndex: "code",
      title: "Code",
      align: "left",
      render: (value, record) =>
        record.type === "color" ? `#${record.code}` : ""
    },
    {
      key: "provider",
      dataIndex: "provider",
      title: "Provider",
      align: "left",
      render: (value, record) =>
        record.type === "media" ? record.provider : ""
    },
    {
      key: "url",
      title: "Path",
      width: 300,
      render: (value, record: ImageRowWithType) => {
        if (record.type !== "media") return null;
        return (
          <a
            onClick={e => {
              e.preventDefault();
              onOpenModal(record.url);
            }}
          >
            {record.isVideo ? (
              <VideoCameraIcon height={80} title="Video" width={80} />
            ) : (
              <img
                key={record.url}
                loading="lazy"
                src={record.url}
                style={{
                  maxWidth: "250px",
                  minHeight: "100px",
                  objectPosition: "center"
                }}
              />
            )}
          </a>
        );
      }
    },
    {
      key: "width",
      dataIndex: "width",
      title: "Width",
      align: "left",
      render: (value, record) => (record.type === "color" ? "" : value)
    },
    {
      key: "height",
      dataIndex: "height",
      title: "Height",
      align: "left",
      render: (value, record) => (record.type === "color" ? "" : value)
    },
    {
      key: "shotTypeDescr",
      dataIndex: "shotTypeDescr",
      title: "Shot Type",
      align: "left"
    },
    {
      key: "category",
      dataIndex: "category",
      title: "Category",
      align: "left"
    },
    {
      key: "fileType",
      dataIndex: "fileType",
      title: "File Type",
      align: "left"
    }
  ];

  const data: ImageRowWithType[] = colors.flatMap(color => {
    const colorRow: ImageRowWithType = {
      key: color.name,
      type: "color",
      color: color.name,
      hex: [color.r, color.g, color.b]
        .map(c => c.toString(16).padStart(2, "0"))
        .join(""),
      code: color.code,
      name: color.name,
      url: "",
      category: "",
      shotType: 0,
      shotTypeDescr: "",
      fileType: "",
      provider: "",
      isIoF: false,
      isVideo: false,
      height: 0,
      width: 0
    };

    const mediaRows: ImageRowWithType[] = color.images.map(media => ({
      id: media.url,
      key: media.url,
      type: "media",
      url: media.url,
      category: media.category,
      height: media.height,
      width: media.width,
      shotType: media.shotType,
      shotTypeDescr: shotTypeLookup?.[String(media.shotType)] ?? "",
      fileType: media.fileType,
      provider: getProvider(media),
      isIoF: isIoF(media),
      isVideo: isVideo(media),
      code: media.colorCode,
      name: media.name,
      color: color.name,
      hex: [color.r, color.g, color.b]
        .map(c => c.toString(16).padStart(2, "0"))
        .join("")
    }));

    return [colorRow, ...mediaRows];
  });
  const disabledRowKeys = data
    .filter(row => (row.type === "color" && row.key !== undefined) || row.isIoF)
    .map(row => row.key as string);

  return (
    <>
      <Table.Table
        enableRowSelection
        columns={columns}
        data={data}
        data-testid="gvm-trim-color-table"
        dataDensity="small"
        disabledSelectedRowKeys={disabledRowKeys}
        displayFilter={true}
        displayPageSizeSelector={true}
        emptyText="No colors available"
        enablePagination={true}
        isLoading={isLoading}
        rowKey="key"
        scrollY={500}
        sortableColumns={true}
        tableLayout="auto"
        onChangeSelectedRows={(selectedKeys, selectedRows) => {
          setSelectedRows(selectedRows);
        }}
      />
      <Modal
        header="Vehicle Media"
        show={showModal}
        size="large"
        onHide={() => setShowModal(false)}
      >
        <img key={url} src={url} style={{ maxWidth: "100%" }} />
      </Modal>
    </>
  );
};
